import { useState, useEffect, useCallback, useRef } from 'react';

const useFetchData = (instruments, apiUrl, intervalTime = 60000) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const isFirstLoad = useRef(true);

  const fetchData = useCallback(async () => {
    if (isFirstLoad.current) {
      setLoading(true);
    }
    setError(null);
    try {
      let baseUrl = null;
      if (apiUrl) {
        try {
          baseUrl = apiUrl.startsWith('/')
            ? new URL(apiUrl, window.location.origin)
            : new URL(apiUrl);
        } catch (urlError) {
          baseUrl = null;
        }
      }
      if (!baseUrl) {
        throw new Error('API URL missing or invalid. Check REACT_APP_API_URL.');
      }

      const requests = instruments.map(inst =>
        fetch(new URL(`/api/data/${inst}`, baseUrl).toString())
          .then(res => {
            if (!res.ok) throw new Error(`Error: Loading ${inst}`);
            return res.json();
          })
      );
      const results = await Promise.all(requests);
      const dataObj = {};
      instruments.forEach((inst, index) => {
        dataObj[inst] = results[index];
      });
      setData(prevData => ({
        ...prevData,
        ...dataObj
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      if (isFirstLoad.current) {
        setLoading(false);
        isFirstLoad.current = false;
      }
    }
  }, [instruments, apiUrl]);

  useEffect(() => {
    fetchData();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {
      fetchData();
    }, intervalTime);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchData, intervalTime]);

  return { data, loading, error };
};

export default useFetchData;
